import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { addIcons } from 'ionicons';
import { sparklesOutline } from 'ionicons/icons';
import {
  ConvertPlaylistDto,
  ConversionStatusDto,
  RematchRequestDto,
  TokensDto,
  UserDto
} from '../../core/models/api.models';
import { PlaylistsService } from '../../core/services/playlists.service';
import { UserService } from '../../core/services/user.service';

@Component({
  selector: 'app-convert',
  templateUrl: './convert.page.html',
  styleUrls: ['./convert.page.scss']
})
export class ConvertPage implements OnInit, OnDestroy {
  form!: FormGroup;

  user?: UserDto | null;
  loading = false;
  conversion?: ConversionStatusDto | null;
  conversionId?: string;
  error?: string;
  pollTimer?: any;
  rematchStates: Record<string, 'idle' | 'loading' | 'error'> = {};

  // Ad + animation state
  showAdFlow = false;
  adCountdown = 10;
  adTimer?: any;
  adDone = false;
  conversionFinished = false;
  finalAnimationVisible = false;
  tokens?: TokensDto | null;

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private playlistsService: PlaylistsService,
    private userService: UserService,
    private route: ActivatedRoute
  ) {
    this.form = this.createForm();
    addIcons({ sparklesOutline });
  }

  ngOnInit(): void {
    const sub = this.userService.loadMe().subscribe(user => {
      this.user = user;
      const defaultMode = user?.isPremium ? 'FULL' : 'ECO';
      const modeControl = this.form.get('mode');
      if (modeControl && !modeControl.dirty) {
        modeControl.patchValue(defaultMode, { emitEvent: false });
      }
    });
    this.subscriptions.push(sub);
    this.setupPlatformSync();
    this.loadTokens();

    const existingConversionId = this.route.snapshot.paramMap.get('conversionId');
    if (existingConversionId) {
      this.conversionId = existingConversionId;
      this.loading = true;
      this.startPolling(existingConversionId);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.adTimer) {
      clearInterval(this.adTimer);
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  }

  setMode(value: 'ECO' | 'FULL'): void {
    const modeControl = this.form.get('mode');
    if (modeControl) {
      modeControl.patchValue(value, { emitEvent: true });
    }
  }

  submit(): void {
    if (this.form.invalid || !this.user) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.user.spotifyConnected && this.form.value.sourcePlatform === 'spotify') {
      this.error = 'You must reconnect Spotify to continue';
      return;
    }
    if (!this.user.youtubeConnected && this.form.value.sourcePlatform === 'youtube') {
      this.error = 'You must reconnect YouTube to continue';
      return;
    }
    if (!this.user.spotifyConnected && this.form.value.targetPlatform === 'spotify') {
      this.error = 'You must reconnect Spotify to continue';
      return;
    }
    if (!this.user.youtubeConnected && this.form.value.targetPlatform === 'youtube') {
      this.error = 'You must reconnect YouTube to continue';
      return;
    }

    const payload = this.form.value as ConvertPlaylistDto;
    this.loading = true;
    this.conversion = null;
    this.conversionId = undefined;
    this.error = undefined;
    this.conversionFinished = false;
    this.finalAnimationVisible = false;
    this.adDone = false;
    this.showAdFlow = !this.user.isPremium;

    if (this.showAdFlow) {
      this.startAdTimer();
    }

    const sub = this.playlistsService.convertPlaylist(payload).subscribe({
      next: res => {
        const normalized = this.normalizeStatus(res);
        this.conversion = normalized;
        this.conversionId = normalized.conversionId;
        this.startPolling(normalized.conversionId);
        this.loadTokens();
      },
      error: err => {
        const status = err?.status;
        if (status === 403) {
          this.error = err?.error?.message || 'This conversion requires a premium upgrade.';
        } else if (status === 400) {
          this.error = err?.error?.message || 'Not enough tokens available for this conversion today.';
        } else if (status === 429) {
          this.error = 'You hit the rate limit. Please retry in a moment.';
        } else {
          this.error = err?.error?.message || 'Conversion failed';
        }
        this.loading = false;
        this.showAdFlow = false;
      }
    });
    this.subscriptions.push(sub);
  }

  requestRematch(item: any): void {
    if (!this.conversionId || !this.conversion || this.conversion.status !== 'COMPLETED') {
      return;
    }

    const sourceId = this.extractTrackId(item?.sourceTrack);
    if (!sourceId) {
      return;
    }

    const currentTargetId = this.extractTrackId(item?.matchedTarget);
    const payload: RematchRequestDto = {
      conversionId: this.conversionId,
      sourcePlatform: this.conversion.sourcePlatform,
      targetPlatform: this.conversion.targetPlatform,
      sourceTrackId: sourceId,
      currentTargetId
    };

    this.setRematchState(sourceId, 'loading');
    this.error = undefined;
    const sub = this.playlistsService.rematch(payload).subscribe({
      next: res => {
        this.conversion = this.normalizeStatus(res);
        this.syncModeFromStatus(this.conversion);
        this.setRematchState(sourceId, 'idle');
        this.loading = false;
        this.loadTokens();
      },
      error: err => {
        if (err?.status === 400) {
          this.error =
            err?.error?.message || 'No hay tokens suficientes para rematch hoy; usa modo ECO o espera al reset diario.';
        } else {
          this.error = err?.error?.message || 'Rematch failed.';
        }
        this.setRematchState(sourceId, 'error');
        this.loading = false;
      }
    });
    this.subscriptions.push(sub);
  }

  rematchStateFor(trackId: string | undefined): 'idle' | 'loading' | 'error' {
    if (!trackId) {
      return 'idle';
    }
    return this.rematchStates[trackId] || 'idle';
  }

  private setRematchState(trackId: string, state: 'idle' | 'loading' | 'error'): void {
    this.rematchStates = {
      ...this.rematchStates,
      [trackId]: state
    };
  }

  private extractTrackId(track: any): string | undefined {
    if (!track) {
      return undefined;
    }
    return track.spotifyId || track.youtubeId || track.id;
  }

  private startAdTimer(): void {
    this.adCountdown = 10;
    if (this.adTimer) {
      clearInterval(this.adTimer);
    }
    this.adTimer = setInterval(() => {
      this.adCountdown -= 1;
      if (this.adCountdown <= 0) {
        clearInterval(this.adTimer);
        this.adDone = true;
        this.handleCompletionGate();
      }
    }, 1000);
  }

  private handleCompletionGate(): void {
    const allowFinish = this.user?.isPremium || this.adDone;
    if (this.conversionFinished && allowFinish) {
      this.finalAnimationVisible = true;
      setTimeout(() => {
        this.loading = false;
      }, 500);
    }
  }

  private createForm() {
    return this.fb.group({
      sourcePlatform: ['spotify', Validators.required],
      targetPlatform: ['youtube', Validators.required],
      playlistUrl: ['', Validators.required],
      mode: ['ECO', Validators.required]
    });
  }

  private setupPlatformSync(): void {
    const sourceControl = this.form.get('sourcePlatform');
    const targetControl = this.form.get('targetPlatform');
    if (!sourceControl || !targetControl) {
      return;
    }

    const opposite = (val: 'spotify' | 'youtube') => (val === 'spotify' ? 'youtube' : 'spotify');
    const syncValues = (changed: 'source' | 'target') => {
      const sourceVal = sourceControl.value;
      const targetVal = targetControl.value;

      if (changed === 'source' && sourceVal === targetVal) {
        targetControl.patchValue(opposite(sourceVal), { emitEvent: false });
      }
      if (changed === 'target' && sourceVal === targetVal) {
        sourceControl.patchValue(opposite(targetVal), { emitEvent: false });
      }
    };

    this.subscriptions.push(
      sourceControl.valueChanges.subscribe(() => syncValues('source')),
      targetControl.valueChanges.subscribe(() => syncValues('target'))
    );

    if (sourceControl.value === targetControl.value) {
      targetControl.patchValue(opposite(sourceControl.value), { emitEvent: false });
    }
  }

  private startPolling(conversionId: string): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.fetchStatus(conversionId);
    this.pollTimer = setInterval(() => this.fetchStatus(conversionId), 1500);
  }

  private fetchStatus(conversionId: string): void {
    const sub = this.playlistsService.getConversionStatus(conversionId).subscribe({
      next: status => {
        this.conversion = this.normalizeStatus(status);
        this.syncModeFromStatus(this.conversion);
        this.loading = status.status !== 'COMPLETED' && status.status !== 'FAILED';
        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          this.conversionFinished = status.status === 'COMPLETED';
          if (this.pollTimer) {
            clearInterval(this.pollTimer);
          }
          this.handleCompletionGate();
          if (status.status === 'FAILED') {
            this.error = status.errorMessage || 'Conversion failed';
            this.loading = false;
            this.showAdFlow = false;
          }
        }
      },
      error: err => {
        const status = err?.status;
        if (status === 429) {
          this.error = 'You hit the rate limit. Please retry in a moment.';
        } else {
          this.error = err?.error?.message || 'Conversion failed';
        }
        this.loading = false;
        this.showAdFlow = false;
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
        }
      }
    });
    this.subscriptions.push(sub);
  }

  private normalizeStatus(status: any): ConversionStatusDto {
    return {
      added: status?.added || status?.results || [],
      skipped: status?.skipped || [],
      current: status?.current ?? null,
      conversionId: status?.conversionId,
      status: status?.status,
      progress: status?.progress,
      errorMessage: status?.errorMessage,
      sourcePlatform: status?.sourcePlatform,
      targetPlatform: status?.targetPlatform,
      sourceTitle: status?.sourceTitle || status?.sourcePlaylistInfo?.name,
      sourceTrackCount: status?.sourceTrackCount || status?.sourcePlaylistInfo?.trackCount,
      targetTitle: status?.targetTitle || status?.targetPlaylistInfo?.name,
      targetPlaylistUrl: status?.targetPlaylistUrl,
      total: status?.total,
      processedCount: status?.processedCount,
      playlistId: status?.playlistId,
      mode: status?.mode
    } as ConversionStatusDto;
  }

  private syncModeFromStatus(status?: ConversionStatusDto | null): void {
    if (!status?.mode) {
      return;
    }
    const modeControl = this.form.get('mode');
    if (modeControl && !modeControl.dirty) {
      modeControl.patchValue(status.mode, { emitEvent: false });
    }
  }

  private loadTokens(): void {
    const sub = this.playlistsService.getMyTokens().subscribe({
      next: res => (this.tokens = res),
      error: () => (this.tokens = null)
    });
    this.subscriptions.push(sub);
  }


  confidenceColor(level: string): string {
    switch (level) {
      case 'HIGH':
        return 'success';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'danger';
      default:
        return 'medium';
    }
  }

  progressPercent(): number {
    if (!this.conversion) {
      return 0;
    }
    if (typeof this.conversion.progress === 'number') {
      return this.conversion.progress;
    }
    if (this.conversion.processedCount && this.conversion.total) {
      return Math.round((this.conversion.processedCount / this.conversion.total) * 100);
    }
    return 0;
  }
}
